import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, LayoutGrid, CalendarDays, Briefcase, TrendingUp, MessageSquareText } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { PERSON_STATUS_META } from "./peopleMeta";
import { PersonOverviewTab } from "./PersonOverviewTab";
import { PersonJobsTab } from "./PersonJobsTab";
import { PersonPerformanceTab } from "./PersonPerformanceTab";
import { PersonFeedbackTab } from "./PersonFeedbackTab";
import { OfficeCalendarTab } from "@/components/org/office/OfficeCalendarTab";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "jobs", label: "Jobs", icon: Briefcase },
  { id: "performance", label: "Performance", icon: TrendingUp },
  { id: "feedback", label: "Feedback", icon: MessageSquareText },
];

export const PersonProfile = ({ userId, API, onBack }) => {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    axios.get(`${API}/org/people/${userId}`, { withCredentials: true })
      .then(({ data }) => setProfile(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, userId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="person-profile-error">{error}</p>;
  if (!profile) return <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="person-profile-loading">Loading…</p>;

  const status = PERSON_STATUS_META[profile.current_status.status] || PERSON_STATUS_META.available;

  return (
    <div data-testid="person-profile">
      <button type="button" onClick={onBack} data-testid="person-profile-back-btn"
        className="mb-4 flex items-center gap-1.5 text-xs font-bold text-slate-500 transition-colors duration-150 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white">
        <ArrowLeft size={14} /> Back to People
      </button>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight text-slate-950 dark:text-white" data-testid="person-profile-name">{profile.name}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="person-profile-role">{profile.role}</p>
        </div>
        <span className={`rounded-full border px-3 py-1.5 text-xs font-bold ${status.cls}`} data-testid="person-profile-status-badge">{status.label}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-white/10" data-testid="person-profile-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`person-profile-tab-${t.id}`}
            className={`flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm font-bold transition-colors duration-150 ${tab === t.id ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "overview" && <PersonOverviewTab profile={profile} />}
        {tab === "calendar" && <OfficeCalendarTab meta={profile} API={API} personMode={{ userId }} />}
        {tab === "jobs" && <PersonJobsTab userId={userId} profile={profile} API={API} />}
        {tab === "performance" && <PersonPerformanceTab />}
        {tab === "feedback" && <PersonFeedbackTab />}
      </div>
    </div>
  );
};
